import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, Alert, Steps, message, Divider, Select, Checkbox } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined, ShopOutlined, GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../../store';
import { register } from '../../store/slices/authSlice';
import { AuthService } from '../../services/auth';

const { Step } = Steps;

interface RegisterForm {
  name: string;           // 商户名称/企业名称
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  business_type: string;
  country: string;
  agree_terms: boolean;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [error, setError] = useState<string>('');
  const [currentStep, setCurrentStep] = useState(0);

  const handleSubmit = async () => {
    try {
      setError('');
      // 多步表单：onFinish 只携带当前已挂载步骤的字段，
      // 必须用 getFieldsValue(true) 取出所有步骤(含已卸载)的完整值
      const values = form.getFieldsValue(true) as RegisterForm;
      const result = await dispatch(register({
        name: values.name,
        email: values.email,
        phone: values.phone,
        password: values.password,
        business_type: values.business_type,
        country: values.country,
        agree_terms: values.agree_terms,
      }));

      if (register.fulfilled.match(result)) {
        message.success(t('auth.registerSuccess'));
        navigate('/dashboard');
      } else {
        setError(result.payload as string || t('auth.registerFailed'));
      }
    } catch (err: any) {
      setError(err.message || t('auth.registerRetry'));
    }
  };

  const nextStep = () => {
    form.validateFields()
      .then(() => {
        setCurrentStep(currentStep + 1);
      })
      .catch(() => {
        message.error(t('auth.fillComplete'));
      });
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const businessTypes = [
    { value: 'individual', label: t('auth.businessIndividual') },
    { value: 'company', label: t('auth.businessCompany') },
    { value: 'non_profit', label: t('auth.businessNonProfit') },
  ];

  const countries = [
    { value: 'US', label: t('auth.countryUS') },
    { value: 'CN', label: t('auth.countryCN') },
    { value: 'HK', label: t('auth.countryHK') },
    { value: 'SG', label: t('auth.countrySG') },
    { value: 'GB', label: t('auth.countryGB') },
    { value: 'JP', label: t('auth.countryJP') },
    { value: 'AU', label: t('auth.countryAU') },
    { value: 'CA', label: t('auth.countryCA') },
    { value: 'DE', label: t('auth.countryDE') },
    { value: 'FR', label: t('auth.countryFR') },
  ];

  const steps = [
    {
      title: t('auth.stepMerchantInfo'),
      content: (
        <>
          <Form.Item
            name="name"
            rules={[{ required: true, message: t('auth.merchantNameRequired') }]}
          >
            <Input
              prefix={<ShopOutlined />}
              placeholder={t('auth.merchantName')}
              autoComplete="organization"
            />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('auth.emailRequired') },
              { type: 'email', message: t('auth.emailInvalid') }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={t('auth.email')}
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="phone"
            rules={[{ required: true, message: t('auth.phoneRequired') }]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder={t('auth.phone')}
              autoComplete="tel"
            />
          </Form.Item>

          <Form.Item
            name="country"
            rules={[{ required: true, message: t('auth.countryRequired') }]}
          >
            <Select
              placeholder={t('auth.selectCountry')}
              options={countries}
              suffixIcon={<GlobalOutlined />}
            />
          </Form.Item>
        </>
      ),
    },
    {
      title: t('auth.stepAccountSetup'),
      content: (
        <>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: t('auth.passwordRequired') },
              { min: 8, message: t('auth.passwordMin8') },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('auth.passwordWithMin')}
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: t('auth.confirmPasswordRequired') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('auth.passwordMismatch')));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('auth.confirmPassword')}
              autoComplete="new-password"
            />
          </Form.Item>
        </>
      ),
    },
    {
      title: t('auth.stepBusinessType'),
      content: (
        <>
          <Form.Item
            name="business_type"
            rules={[{ required: true, message: t('auth.businessTypeRequired') }]}
          >
            <Select
              placeholder={t('auth.selectBusinessType')}
              options={businessTypes}
            />
          </Form.Item>

          <Form.Item
            name="agree_terms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error(t('auth.agreeTermsRequired'))),
              },
            ]}
          >
            <Checkbox>
              {t('auth.agreeTerms')} <a href="/terms" target="_blank">{t('auth.termsOfService')}</a> {t('auth.and')} <a href="/privacy" target="_blank">{t('auth.privacyPolicy')}</a>
            </Checkbox>
          </Form.Item>
        </>
      ),
    },
  ];

  return (
    <div className="auth-page">
      <div className="auth-brand">
        <div className="auth-brand-logo">
          <span className="auth-brand-logo-icon">★</span>
          <span>FastStar</span>
        </div>
        <div className="auth-brand-copy">
          <h2>{t('auth.brandHeadline')}</h2>
          <p>{t('auth.brandSubRegister')}</p>
        </div>
        <div className="auth-brand-footer">{t('auth.brandFooter')}</div>
      </div>

      <div className="auth-form-side">
      <div className="auth-container large">
        <div className="auth-header">
          <div className="auth-logo">Fast<span className="logo-accent">Star</span></div>
          <h1 className="auth-title">{t('auth.registerTitle')}</h1>
          <p className="auth-subtitle">{t('auth.registerSubtitle')}</p>
        </div>

        <Steps current={currentStep} className="register-steps">
          {steps.map(item => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError('')}
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          className="auth-form"
          name="register"
          onFinish={handleSubmit}
          autoComplete="off"
          size="large"
        >
          <div className="step-content">
            {steps[currentStep].content}
          </div>

          <div className="step-actions">
            {currentStep > 0 && (
              <Button onClick={prevStep} style={{ marginRight: 8 }}>
                {t('common.back')}
              </Button>
            )}
            {currentStep < steps.length - 1 && (
              <Button type="primary" onClick={nextStep}>
                {t('common.next')}
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="auth-submit-btn"
              >
                {t('auth.createAccount')}
              </Button>
            )}
          </div>
        </Form>

        <Divider />

        <div className="auth-links">
          <span>{t('auth.haveAccount')}</span>
          <Link to="/login">{t('auth.loginNow')}</Link>
        </div>
      </div>
      </div>
    </div>
  );
};

export default RegisterPage; 